<template>
	<div id="question">
		<v-row no-gutters class="fill-height">
			<!-- sidebar -->
			<v-col lg="2" sm="3" cols="12" class="pa-3">
				<div class="font-weight-bold text-h6">
					الخصائص العامة
				</div>
				
				<!-- data -->
				<div class="mt-4">
					<v-form
						ref="form"
						:disabled="submitLoading || loading"
						@submit.prevent="submit()"
					>
						<v-row dense>
							<!-- class -->
							<v-col sm="12" cols="6" class="mb-sm-2">
								<v-autocomplete
									v-model="mainData.classId"
									label="الصف"
									:items="classes"
									item-text="name"
									item-value="id"
									dense
									outlined
									hide-details
									:rules="rules.requiredAsNumber"
									@change="fetchSubjects(mainData.classId)"
								/>
							</v-col>

							<!-- mainSubjectId -->
							<v-col sm="12" cols="6" class="mb-sm-2">
								<v-autocomplete
									v-model="mainData.mainSubjectId"
									label="المحور"
									:items="subjects"
									item-text="name"
									item-value="id"
									:loading="loadingSubjects"
									dense
									outlined
									hide-details
									:rules="rules.requiredAsNumber"
								/>
							</v-col>

							<!-- subject -->
							<v-col sm="12" cols="6" class="mb-sm-2">
								<v-autocomplete
									v-model="mainData.subjectId"
									label="الامتحان"
									:items="
										mainData.mainSubjectId
											? subjects.find(c => c.id === mainData.mainSubjectId).subjectChilds
											: []
									"
									item-text="name"
									item-value="id"
									:loading="loadingSubjects"
									dense
									outlined
									hide-details
									:rules="rules.requiredAsNumber"
								/>
							</v-col>

							<!-- difficulty -->
							<v-col sm="12" cols="6" class="mb-sm-2">
								<v-autocomplete
									v-model="mainData.difficulty"
									label="الصعوبة"
									:items="questionDifficulties"
									item-text="name"
									item-value="id"
									dense
									outlined
									hide-details
									:rules="rules.requiredAsNumber"
								/>
							</v-col>
						
							<!-- teacher -->
							<v-col sm="12" cols="6" class="mb-sm-2">
								<v-autocomplete
									v-model="mainData.teacherId"
									label="المدرس"
									:items="teachers"
									item-text="fullName"
									item-value="id"
									dense
									outlined
									hide-details
									:rules="rules.requiredAsNumber"
								/>
							</v-col>

							<!-- submit -->
							<v-col v-if="!$route.params.state && !isEditQuestion" cols="12" class="mb-sm-2">
								<v-btn
									type="submit"
									depressed
									width="100%"
									color="primary"
									dense
									:loading="submitLoading || loading"
								>
									<v-icon size="18" class="me-2">$edit</v-icon>
									حفظ البيانات
								</v-btn>
							</v-col>
						</v-row>
					</v-form>
				</div>
			</v-col>
			<v-divider v-if="$vuetify.breakpoint.smAndUp" vertical/>
			
			<!-- content -->
			<v-col
				v-bind="
					!isEditQuestion
						? { lg: 8, sm: 7, cols: 12 }
						: { lg: 10, sm: 9, cols: 12 }
				"
				:class="{ 'question-content': $vuetify.breakpoint.smAndUp }"
			>
				<div
					class="d-flex flex-wrap flex-sm-row flex-column-reverse justify-space-between align-sm-center px-3 py-2"
				>
					<!-- breadcrumbs -->
					<bee-breadcrumbs
						:items="[
							{
								text: 'بنك الأسئلة',
								disabled: false,
								icon: 'mdi-clipboard-text-multiple-outline',
								to: { name: 'questions-bank' } },
							{
								text: `${!isEditQuestion ? 'إضافة أسئلة' : 'تعديل سؤال'}`,
								disabled: true
							},
						]"
						class="mt-sm-0 mt-3"
					/>

					<!-- save & back -->
					<div>
						<!-- save -->
						<v-btn
							v-if="isEditQuestion || $route.params.state"
							depressed
							height="34"
							color="primary"
							class="me-2"
							:disabled="!questions.length"
							:loading="submitLoading"
							@click="!isEditQuestion ? submit() : submitQuestion()"
						>
							حفظ {{!isEditQuestion ? 'الأسئلة' : ''}}
						</v-btn>
						
						<!-- back -->
						<v-btn
							depressed
							height="34"
							@click="$router.go(-1)"
						>
							عودة
						</v-btn>
					</div>
				</div>

				<v-divider/>
				<v-progress-linear v-if="loading" height="4" indeterminate color="primary" />

				<!-- questions -->
				<div
					ref="questionsContainer"
					class="pa-3 overflow-y-auto"
					:style="{ height: `${$vuetify.breakpoint.height - 100}px` }"
				>
					<!-- questions -->
					<div v-if="$route.params.state || isEditQuestion">
						<v-expansion-panels v-model="expandedPanel" flat>
							<template v-for="(question, index) in questions">
								<!-- single question -->
								<v-question-expander
									v-if="question.type === questionType.single"
									v-model="questions[index]"
									:key="question.id"
									:ref="`expander-${question.id}`"
									:hide-delete-btn="isEditQuestion"
									:error="isSubmitted"
									class="rounded"
									:class="{
										'error-border':
											isSubmitted &&
											checkSingleQuestionValidation(question)
									}"
									@click:delete="removeQuestion(index)"
								/>

								<!-- group question -->
								<v-expansion-panel
									v-if="question.type === questionType.group"
									:key="question.id"
									:ref="`expander-${question.id}`"
									class="rounded"
									:class="{
										'error-border':
											isSubmitted &&
											checkGroupQuestionValidation(question)
									}"
								>
									<v-expansion-panel-header>
										<template v-slot:default="{ open }">
											<!-- question text -->
											<div v-if="!open">
												<div
													v-html="question.text || 'نص السؤال'"
													class="question-title font-weight-medium"
												/>

												<div class="d-flex align-center text-caption mt-2">
													<div class="me-2 ">
														<v-icon size="16" class="me-1">mdi-progress-question</v-icon>
														{{questionTypes.find(c => c.id === questionType.group)?.name || 'نوع السؤال'}}
													</div>
													<div class="me-2 text-caption">
														<v-icon size="16" class="me-1">
															mdi-card-multiple-outline
														</v-icon>
														{{
															question.subContent
															.filter(c => c.type === subContentType.question).length
														}} أسئلة
													</div>
													<div v-if="question.image">
														<v-icon size="16" class="me-1">mdi-image-outline</v-icon>
														تم إرفاق صورة
													</div>
												</div>
											</div>

											<!-- question type -->
											<v-chip
												v-else
												color="grey rounded lighten-2"
												outlined
												style="max-width: fit-content;"
											>
												<span class="black--text font-weight-bold">
													{{questionTypes.find(c => c.id === questionType.group)?.name || 'نوع السؤال'}}
												</span>
											</v-chip>
										</template>
									</v-expansion-panel-header>
									<v-expansion-panel-content>
										<!-- question text -->
										<p class="mb-2">نص السؤال</p>
										<div class="d-flex align-start mb-5">
											<!-- image -->
											<bee-drop-img
												v-model="question.image"
												:width="92"
												:height="92"
												clearable
												dashed
												:border-width="2"
												border-color="#cfcfcf"
												drag-text="صورة"
												drop-text="أفلت"
												blurable
												hoverable
												hide-image-name
												hide-details
												:radius="10"
											/>

											<!-- text -->
											<editor
												v-model="question.text"
												ref="editor"
												hide-menubar
												placeholder="Write the question here ..."
												mini-tools
												:error="isSubmitted && !question.text"
												class="flex-grow-1"
											/>
										</div>

										<!-- content order -->
										<div>
											<div
												v-for="(item, subIndex) in question.subContent"
												:key="item.id"
												ref="subContentItems"
												class="d-flex mt-3"
												:class="{ 'drag-item': dragIndex === question.subContent.indexOf(item) }"
												draggable
												@dragstart="
													orderItemId = item.id;
													setDataOnDrag($event, subIndex);
												"
												@dragover.prevent
												@dragend="dragIndex = null"
												@dragenter="dragIndex = subIndex"
												@drop="
													orderItemId !== item.id
													? getDataOnDrop($event, subIndex, question.subContent)
													: null;
												"
											>
												<div class="d-flex align-center ps-1 pe-3">
													<v-icon>mdi-menu</v-icon>
												</div>

												<!-- content icon -->
												<div class="sub-content-icon d-flex align-center rounded-r px-2">
													<v-icon color="white">
														{{getIconDueType(item.type)}}
													</v-icon>
												</div>

												<!-- question -->
												<v-card
													v-if="item.type === subContentType.question"
													min-height="40"
													flat
													class="flex-grow-1 rounded-r-0"
												>
													<v-expansion-panels flat>
														<v-question-expander
															v-model="question.subContent[subIndex]"
															hide-delete-btn
															:error="isSubmitted"
															class="rounded-l rounded-r-0"
															:class="{
																'error-border':
																	isSubmitted &&
																	checkSingleQuestionValidation(
																		question.subContent[subIndex]
																	)
															}"
															@click:delete="removeSubContent(index, subIndex)"
														/>
													</v-expansion-panels>
												</v-card>

												<!-- divider -->
												<v-card
													v-else-if="item.type === subContentType.divider"
													min-height="40"
													flat
													outlined
													class="d-flex align-center flex-grow-1 pa-2 rounded-r-0"
												>
													<hr class="flex-grow-1"/>
												</v-card>

												<!-- text -->
												<v-card
													v-else-if="item.type === subContentType.text"
													min-height="40"
													flat
													outlined
													class="d-flex align-center flex-grow-1 pa-2 rounded-r-0"
												>
													<v-textarea
														v-model="item.text"
														dir="auto"
														auto-grow
														rows="1"
														hide-details
														placeholder="Write the text here ..."
														class="no-input-border flex-grow-1 pt-0"
													/>
												</v-card>

												<!-- image -->
												<v-card
													v-else-if="item.type === subContentType.image"
													min-height="40"
													flat
													outlined
													class="d-flex align-center flex-grow-1 pa-2 rounded-r-0"
												>
													<div class="d-flex flex-column">
														<v-img
															:src="item.preview"
															width="80"
															max-width="80"
															height="80"
															max-height="80"
															class="rounded"
														/>

														<div class="d-flex flex-wrap align-center text-body-2">
															<div class="me-4 font-weight-bold">إعدادات الصورة</div>
														
															<div class="d-flex align-center">
																<span class="me-2">المحاذاة</span>
																<v-btn-toggle dense active-class="primary">
																	<!-- right -->
																	<v-btn
																		:color="item.imageOptions.align === 'right' ? 'primary' : ''"
																		@click="item.imageOptions.align = 'right'"
																	>
																		<v-icon :color="item.imageOptions.align === 'right' ? 'white' : ''">
																			mdi-align-horizontal-right
																		</v-icon>
																	</v-btn>
																	
																	<!-- center -->
																	<v-btn
																		:color="item.imageOptions.align === 'center' ? 'primary' : ''"
																		@click="item.imageOptions.align = 'center'"
																	>
																		<v-icon :color="item.imageOptions.align === 'center' ? 'white' : ''">
																			mdi-align-horizontal-center
																		</v-icon>
																	</v-btn>
																	
																	<!-- left -->
																	<v-btn
																		:color="item.imageOptions.align === 'left' ? 'primary' : ''"
																		@click="item.imageOptions.align = 'left'"
																	>
																		<v-icon :color="item.imageOptions.align === 'left' ? 'white' : ''">
																			mdi-align-horizontal-left
																		</v-icon>
																	</v-btn>
																</v-btn-toggle>
															</div>
														
															<div class="d-flex align-center ms-3">
																<span class="me-2">
																	الأبعاد
														
																	<v-icon>mdi-resize</v-icon>
																</span>

																<div class="d-flex align-center">
																	<v-text-field
																		v-model.number="item.imageOptions.width"
																		label="العرض"
																		prefix="px"
																		outlined
																		dense
																		hide-details
																		style="max-width: 100px"
																		@change="
																			item.imageOptions.height =
																				fixNumber(item.imageOptions.width / item.imageOptions.ratio, 0)
																		"

																	/>
																	<span class="mx-2">×</span>
																	<v-text-field
																		v-model.number="item.imageOptions.height"
																		label="الارتفاع"
																		prefix="px"
																		outlined
																		dense
																		hide-details
																		style="max-width: 100px"
																		@change="
																			item.imageOptions.width =
																				fixNumber(item.imageOptions.height * item.imageOptions.ratio, 0)
																		"
																	/>
																</div>
															</div>
														</div>
													</div>
												</v-card>

												<!-- remove button -->
												<v-btn
													small
													icon
													class="ms-2 pa-1 my-auto"
													@click="removeSubContent(index, subIndex)"
												>
													<v-icon size="22">$close</v-icon>
												</v-btn>
											</div>
										</div>
										
										<!-- group actions -->
										<div class="mt-5 mb-3">
											<p class="font-weight-bold mb-3">
												ما الذي ترغب بإضافته للمجموعة؟
											</p>

											<div>
												<!-- question -->
												<v-btn
													depressed
													color="primary"
													class="me-3"
													@click="addSubContent(index, subContentType.question)"
												>
													<v-icon class="me-1">
														{{getIconDueType(subContentType.question)}}
													</v-icon>
													سؤال جديد
												</v-btn>
												
												<!-- divider -->
												<v-btn
													depressed
													color="primary"
													class="me-3"
													@click="addSubContent(index, subContentType.divider)"
												>
													<v-icon class="me-1">
														{{getIconDueType(subContentType.divider)}}
													</v-icon>
													خط فاصل
												</v-btn>
												
												<!-- text -->
												<v-btn
													depressed
													color="primary"
													class="me-3"
													@click="addSubContent(index, subContentType.text)"
												>
													<v-icon class="me-1">
														{{getIconDueType(subContentType.text)}}
													</v-icon>
													نص
												</v-btn>
												
												<!-- image -->
												<v-btn
													depressed
													color="primary"
													@click="addSubContent(index, subContentType.image)"
												>
													<v-icon class="me-1">
														{{getIconDueType(subContentType.image)}}
													</v-icon>
													صورة
												</v-btn>
											</div>
										</div>

										<template v-if="!isEditQuestion">
											<v-divider class="my-3"/>
											
											<v-btn
												color="grey lighten-2"
												outlined
												@click="removeQuestion(index)"
											>
												<span class="error--text">
													<v-icon size="18">$delete</v-icon>
													حذف السؤال
												</span>
											</v-btn>
										</template>
									</v-expansion-panel-content>
								</v-expansion-panel>
							</template>
						</v-expansion-panels>

						<div v-if="!isEditQuestion" class="mt-2">
							<!-- add new question -->
							<v-menu offset-y>
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										depressed
										color="primary"
										width="140"
										max-width="140"
										v-bind="attrs"
										v-on="on"
									>
										<v-icon class="me-1">$add</v-icon>
										إضافة سؤال
									</v-btn>
								</template>
								<v-list>
									<v-list-item
										v-for="type in questionTypes"
										:key="type.id"
										@click="addQuestion(type.id)"
									>
										<v-list-item-title>{{type.name}}</v-list-item-title>
									</v-list-item>
								</v-list>
							</v-menu>
						</div>
					</div>

					<!-- please select -->
					<div
						v-else
						class="d-flex flex-column align-center mt-6"
					>
						<v-icon size="40" color="primary" class="mb-4">
							mdi-clipboard-text-multiple-outline
						</v-icon>
						<p class="mb-0 font-weight-medium">
							يرجى إدخال الخصائص العامة أولاً
						</p>
					</div>
				</div>
			</v-col>
			
			<!-- live preview -->
			<v-col v-if="!isEditQuestion" sm="2" cols="12" class="pa-2 grey lighten-4">
				<div class="text-end font-weight-bold text-body-2 mb-2">Live preview</div>
				<bee-live-preview
					ref="livePreview"
					:questions="questions"
					:barcode-text="tteessttqq"
				/>
			</v-col>
		</v-row>
	</div>
</template>

<script>
import { questionDifficulties, /* questionDifficulty, */ questionType, questionTypes, subContentType } from '@/helpers/enums';
import { convertFromCmToPx, fixNumber, generateBarcodeString, getDataOnDrop, refactorQuestions, setDataOnDrag } from '@/helpers/functions';
import rules from '@/helpers/validation-rules';
import VQuestionExpander from '@/components/VQuestionExpander.vue';
import BeeLivePreview from '@/components/BeeLivePreview.vue';
import { mapState } from 'vuex';
import { A4Dimensions } from '@/helpers/constants';
export default {
	name: 'Question',
	components: { VQuestionExpander, BeeLivePreview },

	data() {
		return {
			// helpers
			rules,
			questionType,
			questionTypes,
			questionDifficulties,

			expandedPanel: null,
			subContentType,

			orderItemId: null,
			dragIndex: null,

			loading: false,
			isSubmitted: false,
			submitLoading: false,

			// items
			loadingSubjects: false,
			subjects: [],
			
			// data
			questions: [],
			mainData: {
				mainSubjectId: null,
				classId: null,
				difficulty: null,
				subjectId: null,
				teacherId: null,
			},
			tteessttqq: '1235684984616843321',
		};
	},

	computed: {
		...mapState({
			classes: state => state.classes.classes,
			teachers: state => state.teachers.teachers,
		}),

		isEditQuestion() { return Boolean(this.$route.query.questionId); }
	},

	methods: {
		fetchSubjects(classId) {
			if (!classId) return;
			this.subjects = [];
			this.loadingSubjects = true;
			return this.$store.dispatch('subjects/fetchByClassId', { classId }).then(data => {
				this.subjects = data;
			}).finally(() => {
				this.loadingSubjects = false;
			})
		},

		getIconDueType(type) {
			switch (type) {
			case subContentType.question: return 'mdi-help-circle';
			case subContentType.divider: return 'mdi-slash-forward';
			case subContentType.text: return 'mdi-format-text';
			case subContentType.image: return 'mdi-image-outline';
			}
		},

		checkSingleQuestionValidation(question) {
			return (
				!question.text ||
				!question.options.some(c => c.isCorrect) ||
				!question.options.every(c => c.text)
			)
		},

		checkGroupQuestionValidation(question) {
			return (
				!question.text ||
				!question.subContent
					.filter(c => c.type === subContentType.question)
					.every(c =>
						c.text &&
						c.options.some(c => c.isCorrect) &&
						c.options.every(c => c.text)
					)
			)
		},
		
		checkQuestionsValidation() {
			return this.questions.every(c =>
				c.type === questionType.single
					? !this.checkSingleQuestionValidation(c)
					: !this.checkGroupQuestionValidation(c)
			)
		},

		transformQuestionForRequest(question) {
			return {
				name: question.text,
				type: subContentType.question,
				questionImages: question.image ? [{ id: question.imageId, image: question.image }] : [],
				choices: question.options.map(e => ({
					choice: e.text,
					isCorrect: e.isCorrect,
				})),
				childsQuestions: question.subContent.map(e => {
					switch (e.type) {
					case subContentType.question: return {
						id: e.contentId,
						name: e.text,
						type: subContentType.question,
						questionImages: e.image ? [{ id: e.imageId, image: e.image }] : [],
						choices: e.options.map(e => ({
							choice: e.text,
							isCorrect: e.isCorrect,
						})),
					}
					case subContentType.divider: return {
						id: e.contentId,
						name: 'divider',
						type: subContentType.divider,
						questionImages: [],
						choices: [],
					}
					case subContentType.text: return {
						id: e.contentId,
						name: e.text || '',
						type: subContentType.text,
						questionImages: [],
						choices: [],
					}
					case subContentType.image: return {
						id: e.contentId,
						name: '',
						type: subContentType.image,
						questionImages: [{
							id: e.imageId,
							image: e.src || e.preview,
							imageData: ((options) => {
								const data = {
									align: options.align,
									width: options.width,
									height: options.height
								};
								return Object.keys(data)
									.map(key => data[key] ? `${key}:${data[key]}` : '')
									.filter(Boolean)
									.join(';');
							})(e.imageOptions) || ''
						}],
						choices: [],
					}
					default: return null; 
					}
				}).filter(Boolean),
			}
		},

		submit() {
			this.isSubmitted = true;
			if (this.$refs.form.validate() && this.checkQuestionsValidation()) {
				this.submitLoading = true;
				const actionName = !this.$route.params.state ? 'createQuestions' : 'updateQuestions';
				this.$store.dispatch(`questions/${actionName}`, {
					parentSubjectId: this.mainData.mainSubjectId,
					childSubjectId: this.mainData.subjectId,
					specialtyId: this.mainData.classId,
					difficulty: this.mainData.difficulty,
					teacherId: this.mainData.teacherId,
					questions:
						!this.$route.params.state
							? []
							: this.questions.map(c => this.transformQuestionForRequest(c)),
				}).then(() => {
					let message = '';
					if (!this.$route.params.state) {
						this.$router.replace({
							params: { state: 'update-data' },
							query: {
								data: JSON.stringify({
									classId: this.mainData.classId,
									subjectId: this.mainData.subjectId,
									difficulty: this.mainData.difficulty,
									teacherId: this.mainData.teacherId,
								})
							}
						});
						message = '.تم الحفظ. يرجى إدخال الأسئلة';
					} else {
						message = 'تمت إضافة الأسئلة بنجاح';
						this.$router.push({
							name: 'questions-bank',
							query: {
								classId: this.mainData.classId,
								subjectId: this.mainData.subjectId
							}
						})
					}
					this.$snackbar.info(message);
					this.isSubmitted = false;
				}).finally(() => {
					this.submitLoading = false;
				})
			}
		},

		submitQuestion() {
			this.isSubmitted = true;
			if (this.$refs.form.validate() && this.checkQuestionsValidation() && this.questions.length) {
				this.submitLoading = true;
				Promise.all([
					this.$store.dispatch('questions/updateQuestionData', {
						id: this.$route.query.questionId,
						parentSubjectId: this.mainData.mainSubjectId,
						childSubjectId: this.mainData.subjectId,
						specialtyId: this.mainData.classId,
						difficulty: this.mainData.difficulty,
						teacherId: this.mainData.teacherId,
					}),
					this.$store.dispatch('questions/updateQuestion', {
						id: this.$route.query.questionId,
						question: this.transformQuestionForRequest(this.questions[0])
					}),
				]).then(() => {
					this.isSubmitted = false;
					this.$emit('show-snackbar', 'تم التحديث السؤال بنجاح');
					this.$router.go(-1);
				}).finally(() => {
					this.submitLoading = false;
				})
			}
		},

		addQuestion(questionTypeId) {
			// this.$refs.lifePreviewFrame.contentWindow.scrollTo(0, this.$refs.lifePreviewFrame.contentWindow.document.body.scrollHeight);
			this.questions.push({
				id: this.questions.length,
				type: questionTypeId,
				text: '',
				image: null,
				options:
					questionTypeId === questionType.single
						? [{ id: 0, text: '', isCorrect: false }]
						: [],
				subContent: [],
			});
			this.expandedPanel = this.questions.length - 1;
			this.$nextTick(() => {
				this.$refs.questionsContainer.scrollTop = this.$refs.questionsContainer?.scrollHeight;
				setTimeout(() => {
					let editor;
					if (this.$refs[`expander-${this.questions.length - 1}`][0].$refs.editor) {
						editor = this.$refs[`expander-${this.questions.length - 1}`][0].$refs.editor;
					} else {
						editor =
							this.$refs[`expander-${this.questions.length - 1}`][0].$children[1].$children
								.find(c => c.$options.name === 'CKEditor');
					}
					editor?.focus();
				})
			});
		},

		removeQuestion(index) {
			this.questions.splice(index, 1);
			this.questions.forEach((c, i) => 
				c.id > index ? this.questions[i].id-- : null
			)
		},

		addSubContent(questionIndex, typeId, subContentIndex = null) {
			switch (typeId) {
			// question
			case subContentType.question: {
				this.questions[questionIndex].subContent.push({
					id: this.questions[questionIndex].subContent.length,
					type: questionType.single,
					text: '',
					image: null,
					options: [{ id: 0, text: '', isCorrect: false }],
					subContent: [],
				});
				break;
			}
			// divider
			case subContentType.divider: {
				this.questions[questionIndex].subContent.push({
					id: this.questions[questionIndex].subContent.length,
					type: subContentType.divider,
				});
				break;
			}
			// text
			case subContentType.text: {
				this.questions[questionIndex].subContent.push({
					id: this.questions[questionIndex].subContent.length,
					type: subContentType.text,
				});
				this.$nextTick(() => {
					this.$refs
						.subContentItems[this.questions[questionIndex].subContent.length - 1]
						.querySelector('textarea')
						.focus();
				})
				break;
			}
			// image
			case subContentType.image: {
				const imageObj = {
					id: this.questions[questionIndex].subContent.length,
					type: subContentType.image,
					src: null,
					preview: null,
					imageEl: null,
					imageOptions: {
						align: 'left',
						width: 0,
						height: 0,
						ratio: 0,
					}
				};

				// get image
				const input = document.createElement('input');
				input.type = 'file';
				input.accept = 'image/*';
				input.click();
				input.addEventListener('change', (e) => {
					const file = e.target.files[0];
					imageObj.src = file;
					imageObj.preview = URL.createObjectURL(file);
					
					imageObj.imageEl = new Image();
					imageObj.imageEl.src = imageObj.preview;
					imageObj.imageEl.onload = () => {
						imageObj.imageOptions.width = imageObj.imageEl.naturalWidth;
						imageObj.imageOptions.height = imageObj.imageEl.naturalHeight;
						imageObj.imageOptions.ratio = imageObj.imageOptions.width / imageObj.imageOptions.height;
						
						const MaxWidthAllowed = fixNumber(convertFromCmToPx(A4Dimensions.CM.WIDTH), 0);
						if (imageObj.imageOptions.width > MaxWidthAllowed) {
							imageObj.imageOptions.width = MaxWidthAllowed;
							imageObj.imageOptions.height = fixNumber(imageObj.imageOptions.width / imageObj.imageOptions.ratio, 0);
						}
					}
					
					input.remove();
					this.questions[questionIndex].subContent.push(imageObj);
				});
				break;
			}
			}
		},

		removeSubContent(index, subIndex) {
			this.questions[index].subContent.splice(subIndex, 1);
			this.questions[index].subContent.forEach((c, i) => 
				c.id > subIndex ? this.questions[index].subContent[i].id-- : null
			)
		},

		// helpers
		setDataOnDrag,
		getDataOnDrop,
		generateBarcodeString,
		fixNumber
	},

	async created() {
		// eslint-disable-next-line
		const generateQuestion = (type, id, lang, withImage = false, options = {}) => {
			const text = lang === 'ar'
				? 'سيكون عنوان السؤال الرئيسي هنا ، عنوان السؤال الرئيسي سيكون صحيحًا __________.'
				// : 'The Main Question Title Will Be Right Here, The Main Question Title Will Be Right _________________ .';
				: 'Fill in the circle next to the complete sentence';
			const option = lang === 'ar' ? 'الاختيار' : 'some option to test answer';
			/* const difficulty =
				[questionDifficulty.easy, questionDifficulty.medium, questionDifficulty.hard][Math.floor(Math.random() * 3)]
			const teacher = ['أحمد علي', 'سارة محمد', 'خالد يوسف'][Math.floor(Math.random() * 3)]; */
			const image =
				withImage || Math.random() > 0.5 ? 'https://plus.unsplash.com/premium_photo-1664474619075-644dd191935f' : null;
			const correctAnswer = [0, 1, 2, 3][Math.floor(Math.random() * 4)];
			return type === questionType.single
				? {
					id,
					type: questionType.single,
					text,
					image,
					options: [
						{ id: 'a', text: `${option} a`, isCorrect: correctAnswer === 0 },
						{ id: 'b', text: `${option} b`, isCorrect: correctAnswer === 1 },
						{ id: 'c', text: `${option} c`, isCorrect: correctAnswer === 2 },
						{ id: 'd', text: `${option} d`, isCorrect: correctAnswer === 3 },
					],
					// difficulty,
					// teacher,
					subContent: [],
					...options
				}
				: {
					id,
					type: questionType.group,
					text,
					image,
					options: [],
					// difficulty,
					// teacher,
					// subContent: Array.from({ length: 3 }).map((c, i) => generateQuestion(questionType.single, i, lang)),
					subContent: [
						generateQuestion(questionType.single, 1, lang),
						{
							id: 2,
							type: subContentType.divider,
						},
						{
							id: 3,
							type: subContentType.text,
							text: 'test sub text'
						},
						{
							id: 4,
							type: subContentType.image,
							src: image,
							preview: image,
						}
					],
					...options
				}
		};

		// this.questions.push(generateQuestion(questionType.single, this.questions.length, 'en', true))
		// this.questions.push(generateQuestion(questionType.group, this.questions.length, 'en', true))
		/* eslint-disable */
		/* this.questions.push(...[
			{
				"id": 0,
				"type": 1,
				"text": "<p>Choose the best answer</p>",
				"image": null,
				"options": [
				{
					"id": 0,
					"text": "",
					"isCorrect": false
				}
				],
				"subContent": [
				{
					"id": 0,
					"type": 0,
					"text": "<p>We ar at</p>",
					"image": null,
					"options": [
					{
						"id": 0,
						"text": "not",
						"isCorrect": false
					},
					{
						"id": 1,
						"text": "does",
						"isCorrect": false
					},
					{
						"id": 2,
						"text": "school",
						"isCorrect": false
					}
					],
					"subContent": []
				},
				{
					"id": 1,
					"type": 0,
					"text": "<p>The cat is _______ here</p>",
					"image": null,
					"options": [
					{
						"id": 0,
						"text": "what",
						"isCorrect": false
					},
					{
						"id": 1,
						"text": "not",
						"isCorrect": false
					},
					{
						"id": 2,
						"text": "school",
						"isCorrect": false
					}
					],
					"subContent": []
				},
				{
					"id": 2,
					"type": 0,
					"text": "<p>______ does the cats have?</p>",
					"image": null,
					"options": [
					{
						"id": 0,
						"text": "what ",
						"isCorrect": false
					},
					{
						"id": 1,
						"text": "Does",
						"isCorrect": false
					},
					{
						"id": 2,
						"text": "School",
						"isCorrect": false
					}
					],
					"subContent": []
				},
				{
					"id": 3,
					"type": 0,
					"text": "<p>where ________ Nan go?</p>",
					"image": null,
					"options": [
					{
						"id": 0,
						"text": "not",
						"isCorrect": false
					},
					{
						"id": 1,
						"text": "does",
						"isCorrect": false
					},
					{
						"id": 2,
						"text": "what",
						"isCorrect": false
					}
					],
					"subContent": []
				}
				]
			},
			{
				"id": 1,
				"type": 1,
				"text": "<p>Choose the best answer</p>",
				"image": null,
				"options": [
				{
					"id": 0,
					"text": "",
					"isCorrect": false
				}
				],
				"subContent": [
				{
					"id": 0,
					"type": 0,
					"text": "<p>We ar at</p>",
					"image": null,
					"options": [
					{
						"id": 0,
						"text": "not",
						"isCorrect": false
					},
					{
						"id": 1,
						"text": "does",
						"isCorrect": false
					},
					{
						"id": 2,
						"text": "school",
						"isCorrect": false
					}
					],
					"subContent": []
				},
				{
					"id": 1,
					"type": 0,
					"text": "<p>The cat is _______ here</p>",
					"image": null,
					"options": [
					{
						"id": 0,
						"text": "what",
						"isCorrect": false
					},
					{
						"id": 1,
						"text": "not",
						"isCorrect": false
					},
					{
						"id": 2,
						"text": "school",
						"isCorrect": false
					}
					],
					"subContent": []
				},
				{
					"id": 2,
					"type": 0,
					"text": "<p>______ does the cats have?</p>",
					"image": null,
					"options": [
					{
						"id": 0,
						"text": "what ",
						"isCorrect": false
					},
					{
						"id": 1,
						"text": "Does",
						"isCorrect": false
					},
					{
						"id": 2,
						"text": "School",
						"isCorrect": false
					}
					],
					"subContent": []
				},
				{
					"id": 3,
					"type": 0,
					"text": "<p>where ________ Nan go?</p>",
					"image": null,
					"options": [
					{
						"id": 0,
						"text": "not",
						"isCorrect": false
					},
					{
						"id": 1,
						"text": "does",
						"isCorrect": false
					},
					{
						"id": 2,
						"text": "what",
						"isCorrect": false
					}
					],
					"subContent": []
				}
				]
			},
		]) */

		/* this.questions.push(...[
				{
					"id": 0,
					"type": 1,
					"text": "<p>Choose the best answer</p>",
					"image": null,
					"options": [
					{
						"id": 0,
						"text": "",
						"isCorrect": false
					}
					],
					"subContent": [
					{
						"id": 0,
						"type": 0,
						"text": "<p>We ar at</p>",
						"image": null,
						"options": [
						{
							"id": 0,
							"text": "not",
							"isCorrect": false
						},
						{
							"id": 1,
							"text": "does",
							"isCorrect": false
						},
						{
							"id": 2,
							"text": "school",
							"isCorrect": false
						}
						],
						"subContent": []
					},
					{
						"id": 1,
						"type": 0,
						"text": "<p>The cat is _______ here</p>",
						"image": null,
						"options": [
						{
							"id": 0,
							"text": "what",
							"isCorrect": false
						},
						{
							"id": 1,
							"text": "not",
							"isCorrect": false
						},
						{
							"id": 2,
							"text": "school",
							"isCorrect": false
						}
						],
						"subContent": []
					},
					{
						"id": 2,
						"type": 0,
						"text": "<p>______ does the cats have?</p>",
						"image": null,
						"options": [
						{
							"id": 0,
							"text": "what ",
							"isCorrect": false
						},
						{
							"id": 1,
							"text": "Does",
							"isCorrect": false
						},
						{
							"id": 2,
							"text": "School",
							"isCorrect": false
						}
						],
						"subContent": []
					},
					{
						"id": 3,
						"type": 0,
						"text": "<p>where ________ Nan go?</p>",
						"image": null,
						"options": [
						{
							"id": 0,
							"text": "not",
							"isCorrect": false
						},
						{
							"id": 1,
							"text": "does",
							"isCorrect": false
						},
						{
							"id": 2,
							"text": "what",
							"isCorrect": false
						}
						],
						"subContent": []
					}
					]
				},
				{
					"id": 5,
					"type": 0,
					"text": "<p>What to Do __ ?</p>",
					"image": null,
					"options": [
					{
						"id": 0,
						"text": "Jump",
						"isCorrect": true
					},
					{
						"id": 1,
						"text": "run",
						"isCorrect": false
					},
					{
						"id": 2,
						"text": "escape",
						"isCorrect": false
					},
					{
						"id": 3,
						"text": "lay",
						"isCorrect": false
					}
					],
					"subContent": []
				},
				{
					"id": 1,
					"type": 1,
					"text": "<p>Choose the best answer</p>",
					"image": null,
					"options": [
					{
						"id": 0,
						"text": "",
						"isCorrect": false
					}
					],
					"subContent": [
					{
						"id": 0,
						"type": 0,
						"text": "<p>We ar at</p>",
						"image": null,
						"options": [
						{
							"id": 0,
							"text": "not",
							"isCorrect": false
						},
						{
							"id": 1,
							"text": "does",
							"isCorrect": false
						},
						{
							"id": 2,
							"text": "school",
							"isCorrect": false
						}
						],
						"subContent": []
					},
					{
						"id": 1,
						"type": 0,
						"text": "<p>The cat is _______ here</p>",
						"image": null,
						"options": [
						{
							"id": 0,
							"text": "what",
							"isCorrect": false
						},
						{
							"id": 1,
							"text": "not",
							"isCorrect": false
						},
						{
							"id": 2,
							"text": "school",
							"isCorrect": false
						}
						],
						"subContent": []
					},
					// {
					// 	"id": 5,
					// 	"type": 2
					// },
					// {
					// 	"id": 6,
					// 	"type": 4,
					// 	"src": {},
					// 	"preview": "https://ichef.bbci.co.uk/images/ic/1200xn/p0d890hz.png"
					// },
					{
						"id": 2,
						"type": 0,
						"text": "<p>______ does the cats have?</p>",
						"image": null,
						"options": [
						{
							"id": 0,
							"text": "what ",
							"isCorrect": false
						},
						{
							"id": 1,
							"text": "Does",
							"isCorrect": false
						},
						{
							"id": 2,
							"text": "School",
							"isCorrect": false
						}
						],
						"subContent": []
					},
					// {
					// 	"id": 3,
					// 	"type": 0,
					// 	"text": "<p>where ________ Nan go?</p>",
					// 	"image": null,
					// 	"options": [
					// 	{
					// 		"id": 0,
					// 		"text": "not",
					// 		"isCorrect": false
					// 	},
					// 	{
					// 		"id": 1,
					// 		"text": "does",
					// 		"isCorrect": false
					// 	},
					// 	{
					// 		"id": 2,
					// 		"text": "what",
					// 		"isCorrect": false
					// 	}
					// 	],
					// 	"subContent": []
					// }
					]
				},
				{
					"id": 2,
					"type": 1,
					"text": "<p>Solve this</p>",
					"image": 'https://ichef.bbci.co.uk/images/ic/1200xn/p0d890hz.png',
					"options": [],
					"subContent": [
					{
						"id": 0,
						"type": 0,
						"text": "<p>What to start with</p>",
						"image": null,
						"options": [
						{
							"id": 0,
							"text": "-3",
							"isCorrect": true
						},
						{
							"id": 1,
							"text": "-7",
							"isCorrect": false
						},
						{
							"id": 2,
							"text": "x",
							"isCorrect": false
						},
						],
						"subContent": []
					},
					// {
					// 	"id": 1,
					// 	"type": 2
					// },
					{
						"id": 2,
						"type": 2,
						"text": "Use this"
					},
					{
						"id": 3,
						"type": 3,
						"src": {},
						"preview": "https://study.com/cimages/videopreview/videopreview-full/uj16yqmbw4.jpg"
					},
					// {
					// 	"id": 5,
					// 	"type": 2
					// },
					{
						"id": 4,
						"type": 0,
						"text": "<p>What is the value of x</p>",
						"image": null,
						"options": [
						{
							"id": 0,
							"text": "-2",
							"isCorrect": true
						},
						{
							"id": 1,
							"text": "2",
							"isCorrect": false
						},
						{
							"id": 2,
							"text": "5",
							"isCorrect": false
						},
						{
							"id": 3,
							"text": "0",
							"isCorrect": false
						}
						],
						"subContent": []
					}
					]
				},
				{
					"id": 7,
					"type": 0,
					"text": "<p>What to Do __ ?</p>",
					"image": null,
					"options": [
					{
						"id": 0,
						"text": "Jump",
						"isCorrect": true
					},
					{
						"id": 1,
						"text": "run",
						"isCorrect": false
					},
					{
						"id": 2,
						"text": "escape",
						"isCorrect": false
					},
					{
						"id": 3,
						"text": "lay",
						"isCorrect": false
					}
					],
					"subContent": []
				},
				{
					"id": 8,
					"type": 0,
					"text": "<p>What to Do __ ?</p>",
					"image": null,
					"options": [
					{
						"id": 0,
						"text": "Jump",
						"isCorrect": true
					},
					{
						"id": 1,
						"text": "run",
						"isCorrect": false
					},
					{
						"id": 2,
						"text": "escape",
						"isCorrect": false
					},
					{
						"id": 3,
						"text": "lay",
						"isCorrect": false
					}
					],
					"subContent": []
				},
				{
					"id": 9,
					"type": 0,
					"text": "<p>What to Do __ ?</p>",
					"image": null,
					"options": [
					{
						"id": 0,
						"text": "Jump",
						"isCorrect": true
					},
					{
						"id": 1,
						"text": "run",
						"isCorrect": false
					},
					{
						"id": 2,
						"text": "escape",
						"isCorrect": false
					},
					{
						"id": 3,
						"text": "lay",
						"isCorrect": false
					}
					],
					"subContent": []
				},
		]) */
		/* eslint-enable */
		/* window.generateQ = () => {
			this.tteessttqq = generateBarcodeString({
				questionsCountPerPaper: this.questions.reduce((res, a) => {
					if (a.type === 1) {
						res += 1;
					} else if (a.type === 2) {
						res += a.subContent.filter(c => c.type === 1).length;
					}
					return res;
				}, 0),
				questionsCountTotal: this.questions.reduce((res, a) => {
					if (a.type === 1) {
						res += 1;
					} else if (a.type === 2) {
						res += a.subContent.filter(c => c.type === 1).length;
					}
					return res;
				}, 0),
				paperCount: 1,
				examId: 21655,
				subjectId: 311,
				studentId: 5660,
			})
		}
		window.generateQ();
		setTimeout(() => {
			this.$refs.livePreview.printPreview();
		}, 500) */

		const fetchData = (classId, subjectId) => {
			this.mainData.classId = +classId || null;
			return this.fetchSubjects(this.mainData.classId).then(() => {
				this.mainData.subjectId = +subjectId || null;
				this.mainData.mainSubjectId =
					this.subjects.find(c =>
						c.subjectChilds.find(c => c.id === this.mainData.subjectId)
					)?.id || null;
			})
		}

		if (
			this.$route.query.classId &&
			this.$route.query.subjectId
		) {
			fetchData(this.$route.query.classId, this.$route.query.subjectId);
		}
		
		if (!this.isEditQuestion) {
			if (this.$route.params.state) {
				this.loading = true;
				try {
					const data = JSON.parse(this.$route.query.data);
					await fetchData(data.classId, data.subjectId);
					this.mainData.difficulty = +data.difficulty;
					this.mainData.teacherId = +data.teacherId;
				} catch (error) {
					console.error(error);
				} finally {
					this.loading = false;
				}
			}
		} else {
			this.loading = true;
			this.$store.dispatch('questions/fetchQuestionById', {
				id: this.$route.query.questionId
			}).then(data => {
				this.mainData.difficulty = data.difficulty;
				this.mainData.teacherId = data.teacher.id;
				this.questions = refactorQuestions([data]);
			}).finally(() => {
				this.loading = false;
			})
		}
	},
}
</script>

<style lang="scss">
#question {
	height: 100%;

	.question-content {
		border-left: 1px solid #e0e0e0;
	}
	.v-expansion-panel {
		border: 1px solid #e0e0e0;
		margin-top: 0;
		&:not(:last-child) { margin-bottom: 12px; }
		&:not(:first-child)::after { content: unset; }
	}
}
</style>